// @ts-strict-ignore
import omit from 'lodash/omit';
import {ParamComponent} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

import {ButtonAttrs, buttonParams} from '../button';
import {template} from './template';

type AsyncButtonState = {
  isWaiting: boolean;
};

type ButtonParamsType = ButtonAttrs & {
  asyncAction: () => Promise<void>;
};

type AsyncButtonJsxProps = {
  params: ButtonParamsType;
};

export class AsyncButton extends ParamComponent<ButtonParamsType, AsyncButtonState> {
  get config() {
    return Object.assign(super.config, {
      template,
      useShadowDom: true,
      defaultState: {
        isWaiting: false,
      },

      params: {
        ...buttonParams,
        asyncAction: {
          type: Function,
          required: true,
        },
      },

      helpers: {
        ...super.config.helpers,
        getButtonAttrs: () => ({
          ...omit(this.params, `asyncAction`),
          ...(this.params.elref ? {elref: `${this.params.elref}Inner`} : {}),
          icon: this.state.isWaiting ? `spinner` : this.params.icon || ``,
          disabled: this.helpers.isDisabled(),
        }),
        handleButtonClick: () => this.runAsyncAction(),
        isDisabled: () => this.params.disabled || this.state.isWaiting,
      },
    });
  }

  updatePointerEvents() {
    // We want to disable the outer mp-async-button click events when the inner mp-button is disabled
    this.style.pointerEvents = this.helpers.isDisabled() ? `none` : ``;
  }

  setIsWaiting(isWaiting) {
    this.update({isWaiting});
    this.updatePointerEvents();
  }

  async runAsyncAction() {
    if (!this.params.asyncAction) {
      return;
    }
    this.setIsWaiting(true);
    try {
      await this.params.asyncAction();
    } finally {
      this.setIsWaiting(false);
    }
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `disabled`) {
      this.updatePointerEvents();
    }
  }
}

export const AsyncButtonEl = defineAndMakeJsxFactory<AsyncButtonJsxProps>(`mp-async-button`, AsyncButton);
export const AsyncButtonReact = panel2react<AsyncButtonJsxProps>(`mp-async-button`, AsyncButton);
